import { forwardRef, } from "react";
import styled, { css, } from "styled-components";
import { getTheme, } from "@bracket/ui/themes";
import { Box, } from "@bracket/ui/nodes";
import type { InputHTMLAttributes, } from "react";

export declare type SliderProps = {
	min?: number;
	max?: number;
	step?: number;
	value?: number;
	disabled?: boolean;
	onChange?: (value: number) => void;
	trackColor?: string;
	fillColor?: string;
	thumbColor?: string;
	size?: "small" | "medium" | "large";
} & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "size" | "type" | "min" | "max" | "step" | "value">;


/**
 * Slider Component
 *
 * A customizable horizontal slider component with theme-aware styling.
 *
 * @param {SliderProps} props - The props for the slider component
 * @param {number} props.min - Minimum value (default: 0)
 * @param {number} props.max - Maximum value (default: 100)
 * @param {number} props.step - Step increment (default: 1)
 * @param {number} props.value - Current value
 * @param {boolean} props.disabled - Whether the slider is disabled
 * @param {function} props.onChange - Callback when value changes
 * @param {string} props.trackColor - Color of unfilled track portion
 * @param {string} props.fillColor - Color of filled track portion
 * @param {string} props.thumbColor - Color of the draggable thumb
 * @param {"small" | "medium" | "large"} props.size - Size variant
 * @returns {JSX.Element} The rendered slider component
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(({
	min = 0,
	max = 100,
	step = 1,
	value = 0,
	disabled = false,
	onChange,
	trackColor,
	fillColor,
	thumbColor = getTheme().primary.default,
	size = "medium",
	className,
	...rest
}, ref) => {
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if(disabled) return;
		onChange?.(Number(e.target.value));
	};

	const percentage = ((Number(value) - Number(min)) / (Number(max) - Number(min))) * 100;

	const defaultTrackColor = trackColor || getTheme().outline.inactive;
	const defaultFillColor = fillColor || getTheme().primary.default;
	const defaultThumbColor = thumbColor || getTheme().primary.default;

	return(
		<SliderContainer className={className} $disabled={disabled}>
			<StyledSliderInput
				ref={ref}
				type="range"
				min={min}
				max={max}
				step={step}
				value={value}
				disabled={disabled}
				onChange={handleChange}
				$size={size}
				$percentage={percentage}
				$trackColor={defaultTrackColor}
				$fillColor={defaultFillColor}
				$thumbColor={defaultThumbColor}
				{...rest}
			/>
			<TrackBackground
				$disabled={disabled}
				$size={size}
				$trackColor={defaultTrackColor}
				$fillColor={defaultFillColor}
				$percentage={percentage}
			/>
		</SliderContainer>
	);
});

Slider.displayName = "Slider";

const SliderContainer = styled(Box)<{ $disabled: boolean }>`
	align-items: center;
	justify-content: center;
	position: relative;
	width: 100%;
	display: flex;
	height: 40px;
	cursor: ${({ $disabled, }) => ($disabled ? "not-allowed" : "pointer")};
	user-select: none;
`;

const StyledSliderInput = styled.input<{
	$size: "small" | "medium" | "large";
	$percentage: number;
	$trackColor: string;
	$fillColor: string;
	$thumbColor: string;
}>`
	appearance: none;
	width: 100%;
	height: 100%;
	padding: 0;
	position: absolute;
	pointer-events: none;
	background: transparent;
	z-index: 2;
	left: 0;
	top: 0;
	margin: 0;
	vertical-align: middle;

	/* Thumb (handle) styling */
	&::-webkit-slider-thumb {
		appearance: none;
		pointer-events: auto;
		cursor: pointer;
		border-radius: 50%;
		border: none;
		background-color: ${({ $thumbColor, }) => $thumbColor};
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
		transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

		${({ $size, }) => {
			switch($size) {
				case "small":
					return css`
						width: 12px;
						height: 12px;
					`;
				case "large":
					return css`
						width: 20px;
						height: 20px;
					`;
				default:
					return css`
						width: 16px;
						height: 16px;
					`;
			}
		}}

		&:hover {
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
		}

		&:active {
			box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
		}
	}

	&::-moz-range-thumb {
		pointer-events: auto;
		cursor: pointer;
		border-radius: 50%;
		border: none;
		background-color: ${({ $thumbColor, }) => $thumbColor};
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
		transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

		${({ $size, }) => {
			switch($size) {
				case "small":
					return css`
						width: 12px;
						height: 12px;
					`;
				case "large":
					return css`
						width: 20px;
						height: 20px;
					`;
				default:
					return css`
						width: 16px;
						height: 16px;
					`;
			}
		}}

		&:hover {
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
		}

		&:active {
			box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
		}
	}

	/* Track styling */
	&::-webkit-slider-runnable-track {
		${({ $size, }) => {
			switch($size) {
				case "small":
					return css`
						height: 4px;
					`;
				case "large":
					return css`
						height: 8px;
					`;
				default:
					return css`
						height: 6px;
					`;
			}
		}}
		border-radius: 999px;
		background: linear-gradient(
			to right,
			${({ $fillColor, }) => $fillColor} 0%,
			${({ $fillColor, }) => $fillColor} ${({ $percentage, }) => $percentage}%,
			${({ $trackColor, }) => $trackColor} ${({ $percentage, }) => $percentage}%,
			${({ $trackColor, }) => $trackColor} 100%
		);
		transition: background 0.1s ease-out;
	}

	&::-moz-range-track {
		background: transparent;
		border: none;
	}

	&::-moz-range-progress {
		${({ $size, }) => {
			switch($size) {
				case "small":
					return css`
						height: 4px;
					`;
				case "large":
					return css`
						height: 8px;
					`;
				default:
					return css`
						height: 6px;
					`;
			}
		}}
		background-color: ${({ $fillColor, }) => $fillColor};
		border-radius: 999px;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;

		&::-webkit-slider-thumb {
			cursor: not-allowed;
		}

		&::-moz-range-thumb {
			cursor: not-allowed;
		}
	}
`;

const TrackBackground = styled.div<{
	$disabled: boolean;
	$size: "small" | "medium" | "large";
	$trackColor: string;
	$fillColor: string;
	$percentage: number;
}>`
	position: absolute;
	width: 100%;
	left: 0;
	top: 50%;
	transform: translateY(-50%);
	border-radius: 999px;
	pointer-events: none;
	z-index: 0;

	${({ $size, }) => {
		switch($size) {
			case "small":
				return css`
					height: 4px;
				`;
			case "large":
				return css`
					height: 8px;
				`;
			default:
				return css`
					height: 6px;
				`;
		}
	}}

	background: ${({ $trackColor, }) => $trackColor};

	${({ $disabled, }) => {
		if($disabled) {
			return css`
				opacity: 0.5;
			`;
		}
		return "";
	}}
`;

